import * as THREE from 'three';
import { SceneNode } from '../../core/SceneNode';
import { ThreeGeomFactory } from '../../shapes/ThreeGeomFactory';
import type { BridgePin } from '../../../embed/MapBridge';
import { createMapMarkerTube } from './MapMarkerGeometry';

const GORGE_STACK_IDS = new Set([24720, 26378, 26388, 26418]);

/** The dense gorge stories share one physical signpost instead of overlapping cards. */
export function isGorgeStackPin(pin: BridgePin): boolean {
    if (GORGE_STACK_IDS.has(pin.id)) return true;
    // A future manual Tschaukofinger POI joins automatically; it is not in today's feed.
    return /tschaukofinger|tschauko.?finger/i.test(pin.title.trim());
}

export function gorgeStackRank(pin: BridgePin): number {
    if (/tschaukofinger|tschauko.?finger/i.test(pin.title)) return 0;
    if (pin.id === 26388) return 1;
    if (pin.id === 24720) return 2;
    if (pin.id === 26418) return 3;
    if (pin.id === 26378) return 4;
    return 5;
}

function stackBackground(pin: BridgePin): string {
    if (pin.kind === 'story') return '#7C3158';
    switch (pin.subcategory) {
        case 'waterfall': return '#2E718C';
        case 'cave': return '#8A623F';
        case 'viewpoint': return '#B3533D';
        default: return pin.color || '#3D6476';
    }
}

function drawStorySymbol(context: CanvasRenderingContext2D): void {
    context.save();
    context.strokeStyle = '#FFFFFF';
    context.lineWidth = 13;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.beginPath();
    context.moveTo(48, 63);
    context.quadraticCurveTo(89, 47, 126, 79);
    context.quadraticCurveTo(167, 47, 208, 63);
    context.lineTo(208, 193);
    context.quadraticCurveTo(167, 179, 126, 207);
    context.quadraticCurveTo(89, 179, 48, 193);
    context.closePath();
    context.stroke();
    context.beginPath();
    context.moveTo(126, 79);
    context.lineTo(126, 207);
    context.stroke();
    context.restore();
}

function createStackTexture(pin: BridgePin, iconUrl?: string): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('2D canvas unavailable for gorge POI marker');
    const paintBackground = () => {
        context.clearRect(0, 0, 256, 256);
        context.fillStyle = stackBackground(pin);
        context.fillRect(0, 0, 256, 256);
    };
    paintBackground();
    drawStorySymbol(context);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;
    if (iconUrl) {
        const image = new Image();
        image.decoding = 'async';
        image.onload = () => {
            paintBackground();
            const width = image.naturalWidth || 1;
            const height = image.naturalHeight || 1;
            const scale = Math.min(224 / width, 224 / height);
            context.drawImage(image, (256 - width * scale) * 0.5, (256 - height * scale) * 0.5, width * scale, height * scale);
            texture.needsUpdate = true;
        };
        image.src = iconUrl;
    }
    return texture;
}

/** One independently pickable cube in the shared gorge signpost. */
export class MapPoiStackPinNode extends SceneNode {
    private readonly localBounds: THREE.Box3;

    constructor(pin: BridgePin, cubeSize: number, poleHeight: number, iconUrl?: string) {
        super(`MapPoiStackPin_${pin.id}`);
        const group = new THREE.Group();
        group.name = this.name;
        group.userData.sceneNode = this;
        group.userData.bridgePinId = pin.id;

        const geometry = ThreeGeomFactory.createRoundedBoxWithBeveledEdges(
            cubeSize, cubeSize, cubeSize, cubeSize * 0.08, cubeSize * 0.025, 1,
        );
        const material = new THREE.MeshStandardMaterial({
            map: createStackTexture(pin, iconUrl),
            roughness: 0.72,
            metalness: 0.02,
        });
        const cube = new THREE.Mesh(geometry, material);
        cube.name = `GorgeCube_${pin.id}`;
        cube.castShadow = true;
        cube.receiveShadow = true;
        cube.userData.bridgePinId = pin.id;
        group.add(cube);

        if (poleHeight > 0) {
            const pole = createMapMarkerTube(-cubeSize * 0.5, -cubeSize * 0.5 - poleHeight, cubeSize * 0.018);
            pole.name = `GorgePole_${pin.id}`;
            group.add(pole);
        }
        this._mesh = group;
        this.localBounds = new THREE.Box3(
            new THREE.Vector3(-cubeSize * 0.5, -cubeSize * 0.5 - poleHeight, -cubeSize * 0.5),
            new THREE.Vector3(cubeSize * 0.5, cubeSize * 0.5, cubeSize * 0.5),
        );
    }

    protected calculateBoundingBox(): THREE.Box3 { return this.localBounds.clone(); }

    protected override onDestroy(): void {
        this._mesh?.traverse((object: THREE.Object3D) => {
            const mesh = object as THREE.Mesh;
            mesh.geometry?.dispose?.();
            const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
            for (const material of materials) {
                (material as THREE.MeshStandardMaterial).map?.dispose();
                material.dispose();
            }
        });
        this._mesh?.removeFromParent();
        this._mesh = null;
        super.onDestroy();
    }
}
